// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CancelResult } from "@ces/App/Handlers/OrderDetails/xCancelOrder";
import { Integer } from "@ces/App/Widgets/SearchEngineOptimization/Type/schema";
import isApiError from "@insite/client-framework/Common/isApiError";
import {
    ApiParameter,
    doesNotHaveExpand,
    get,
    patch,
    post,
    ServiceResult,
} from "@insite/client-framework/Services/ApiService";
import {
    AddRmaApiParameter,
    GetOrderApiParameter,
    GetOrdersApiParameter,
    UpdateOrderApiParameter,
} from "@insite/client-framework/Services/OrderService";
import { OrderCollectionModel, OrderModel, RmaModel } from "@insite/client-framework/Types/ApiModels";

const ordersUrl = "/api/ces/orderhistory";
const reorderUrl = "/api/ces/orderhistory/reorder";
const cancelUrl = "/api/ces/orderhistory/cancel";
const addRmaUrl = "/api/ces/rma/add";

export async function getOrders(parameter: GetOrdersApiParameter): Promise<OrderCollectionModel> {
    // can't send empty status array to API
    if (parameter.status && parameter.status.length === 0) {
        delete parameter.status;
    }

    delete (parameter as any).onComplete;
    const orders = await get<OrderCollectionModel>(ordersUrl, {
        ...parameter,
        timestamp: `${new Date().getTime()}`,
    } as ApiParameter);
    orders.orders?.forEach(o => {
        cleanOrder(o);
    });
    return orders;
}

export async function getOrder(parameter: GetOrderApiParameter): Promise<OrderModel> {
    const { orderNumber, ...newParameter } = parameter;

    const order = await get<OrderModel>(`${ordersUrl}/detail`, {
        ...parameter,
        timestamp: `${new Date().getTime()}`,
    } as ApiParameter);
    cleanOrder(order, parameter);
    return order;
}

export async function updateOrder(parameter: UpdateOrderApiParameter) {
    const order = parameter.order;
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;
    const orderModel = await patch<OrderModel>(`${ordersUrl}/${orderNumber}`, order);
    cleanOrder(orderModel);
    return orderModel;
}

export async function addRma(parameter: AddRmaApiParameter): Promise<ServiceResult<RmaModel>> {
    try {
        const rmaModel = await post<RmaModel>(`${addRmaUrl}`, parameter.rmaModel);
        return {
            successful: true,
            result: rmaModel,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

function cleanOrder(orderModel: OrderModel, parameter?: { expand?: string[]; additionalExpands?: string[] }) {
    orderModel.orderDate = new Date(orderModel.orderDate);
    orderModel.id = cleanOrderFieldNewLine(orderModel.erpOrderNumber) || orderModel.webOrderNumber;
    if (doesNotHaveExpand(parameter, "orderLines")) {
        delete orderModel.orderLines;
    }

    if (!orderModel.shipViaDescription) {
        delete orderModel.shipViaDescription;
    }
}

function cleanOrderFieldNewLine(field: string) {
    // \r\n = CR + LF → Used as a new line character in Windows
    // \r = CR (Carriage Return) → Used as a new line character in Mac OS before X
    // \n = LF (Line Feed) → Used as a new line character in Unix/Mac OS X
    return field.trim().replace(/\r\n/gi, "").replace(/\r/gi, "").replace(/\n/gi, "");
}

export async function reorder(parameter: GetOrderApiParameter): Promise<ServiceResult<OrderModel>> {
    try {
        const result = await post<GetOrderApiParameter, OrderModel>(reorderUrl, parameter);
        return {
            successful: true,
            result,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export interface GetCancelOrderApiParameter extends ApiParameter {
    orderNumber: string;
}
export async function cancelOrder(parameter: GetCancelOrderApiParameter): Promise<ServiceResult<OrderModel>> {
    try {
        const result = await get<OrderModel>(`${cancelUrl}`, parameter);
        return {
            successful: true,
            result,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}
